import React, {useState} from "react";
import OverlayBase from "../../overlay/OverlayBase";
import SvgLoader from "../../../../svg/SvgLoader";
import {useSocket} from "../../SocketContext";
import {rpc_giveItem} from "../../rpc";
import {getItemDisplayName, getItemIconUrl} from "./itemResolver";

interface GiveItemOverlayProps {
    steamId: string
    playerName?: string
    hide: () => void
}

const SHORTNAME_REGEX = /^[a-zA-Z0-9._-]+$/
const SKIN_REGEX = /^[0-9]+$/

export default function GiveItemOverlay({steamId, playerName, hide}: GiveItemOverlayProps) {
    const socket = useSocket()

    const [shortname, setShortname] = useState("")
    const [amount, setAmount] = useState<string>("1")
    const [skinId, setSkinId] = useState("")
    const [iconFailed, setIconFailed] = useState(false)

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [validationError, setValidationError] = useState("")

    const trimmedShortname = shortname.trim()
    const parsedAmount = parseInt(amount, 10)
    const trimmedSkin = skinId.trim()

    const previewName = trimmedShortname ? getItemDisplayName(trimmedShortname) : ""
    const showIconPreview = trimmedShortname && SHORTNAME_REGEX.test(trimmedShortname)

    const handleGive = async () => {
        if (loading) return
        setError("")
        setValidationError("")

        if (!trimmedShortname) {
            setValidationError("Item shortname is required.")
            return
        }
        if (!SHORTNAME_REGEX.test(trimmedShortname)) {
            setValidationError("Shortname may only contain letters, digits, '.', '_' or '-'.")
            return
        }
        if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            setValidationError("Amount must be a positive integer.")
            return
        }
        if (trimmedSkin && !SKIN_REGEX.test(trimmedSkin)) {
            setValidationError("Skin ID must be numeric.")
            return
        }

        setLoading(true)
        const result = await rpc_giveItem(
            socket,
            steamId,
            trimmedShortname,
            parsedAmount,
            trimmedSkin || undefined
        )
        setLoading(false)

        if (result.success) {
            hide()
            return
        }

        let err = `Error: ${result.message ?? "Unknown error (-1)"}`
        if (result.message === "not_connected") {
            err = `The game server is not connected to RustServerTool (${err})`
        } else if (result.message === "server_timeout") {
            err = `The game server did not respond in time (${err})`
        } else if (result.message === "invalid_argument") {
            err = "Shortname or skin contains invalid characters."
        }
        setError(err)
    }

    return (
        <OverlayBase hide={hide} w="w-[28rem]">
            <h2 className="text-xl font-semibold mb-4">
                Give item to {playerName ? `'${playerName}'` : steamId}
            </h2>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Target Steam ID</label>
                    <input
                        type="text"
                        value={steamId}
                        readOnly
                        className="w-full rst-input text-neutral-400 cursor-not-allowed"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">Item shortname</label>
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <input
                                type="text"
                                value={shortname}
                                onChange={e => { setShortname(e.target.value); setIconFailed(false) }}
                                placeholder="e.g. rifle.ak"
                                className="w-full rst-input"
                                autoFocus
                            />
                            {previewName && (
                                <p className="text-xs text-neutral-400 mt-1">{previewName}</p>
                            )}
                        </div>
                        <div className="size-11 shrink-0 bg-neutral-800 border border-neutral-500 rounded flex items-center justify-center overflow-hidden">
                            {showIconPreview && !iconFailed ? (
                                <img
                                    src={getItemIconUrl(trimmedShortname)}
                                    alt={previewName}
                                    className="size-full object-contain p-1"
                                    onError={() => setIconFailed(true)}
                                />
                            ) : (
                                <span className="text-neutral-600 text-xl">?</span>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">Amount</label>
                    <input
                        type="number"
                        min={1}
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        className="w-full rst-input"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">Skin ID <span className="text-neutral-500 font-normal">(optional)</span></label>
                    <input
                        type="text"
                        value={skinId}
                        onChange={e => setSkinId(e.target.value)}
                        placeholder="Numeric workshop skin ID"
                        className="w-full rst-input"
                    />
                </div>

                {validationError && <p className="text-red-500 text-sm">{validationError}</p>}
            </div>

            <div className="mt-6 flex flex-col items-end">
                {error && <p className="text-red-500 text-sm mb-2 text-right">{error}</p>}
                <div className="flex justify-end gap-3">
                    <button onClick={hide}
                            className="px-4 py-2 hover:bg-neutral-700 border border-neutral-500 rounded-lg">
                        Cancel
                    </button>
                    <button onClick={handleGive} disabled={loading}
                            className="px-4 py-2 bg-primary hover:bg-primary-hover text-white rounded-lg disabled:bg-neutral-600 flex items-center">
                        {loading
                            ? <SvgLoader className="size-5 text-transparent fill-white text-neutral-700 animate-spin inline"/>
                            : "Give"}
                    </button>
                </div>
            </div>
        </OverlayBase>
    )
}
